import React, { forwardRef } from 'react';
import type { InputAdornmentProps as MuiInputAdornmentProps } from '@mui/material';
import { InputAdornment as MuiInputAdornment } from '@mui/material';
import { isEmpty } from 'lodash';

import { reportErrorInProductionOrThrow } from '~/Utils';

/**
 * Explicit InputAdornment props based on actual usage in the codebase.
 */
export interface InputAdornmentProps {
  /** The position this adornment should appear relative to the `Input`. */
  position: MuiInputAdornmentProps['position'];
  /** The content of the adornment (typically an icon, button, or short text). */
  children?: React.ReactNode;
  /** CSS class name to apply to the root element. */
  className?: string;
}

/**
 * This component is a wrapper on the MUI component.
 * This component should be imported and not MUI directly!
 */
const InputAdornment = forwardRef<HTMLDivElement, InputAdornmentProps>(
  ({ position, children, className, ...restProps }, ref) => {
    if (!isEmpty(restProps)) {
      reportErrorInProductionOrThrow(
        `Unexpected props passed to InputAdornment: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the InputAdornmentProps interface and to the component's signature.`,
        true
      );
    }
    return (
      <MuiInputAdornment position={position} className={className} ref={ref} {...restProps}>
        {children}
      </MuiInputAdornment>
    );
  }
);

InputAdornment.displayName = 'InputAdornment';

// Forward MUI's component marker so anything that detects adornments via
// `type.muiName` keeps working through this forwardRef wrapper (in the pinned
// v4 this marker is unset, so the assignment is a no-op, but it stays correct
// if MUI ever starts setting it).
(InputAdornment as { muiName?: string }).muiName = (MuiInputAdornment as { muiName?: string }).muiName;

export default InputAdornment;
